import { doc, getDoc, setDoc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';

/**
 * Add a vote - stores in user's contributions document
 * @param {string} rinkId - ID of the rink
 * @param {string} userId - Anonymous user ID
 * @param {string} entityType - 'location' or 'trail'
 */
export const addVote = async (rinkId, userId, entityType = 'location') => {
    try {
        const userDocRef = doc(db, 'user_contributions', userId);

        const vote = {
            rinkId,
            entityType,
            timestamp: Timestamp.now(),
            counted: false // Will be set to true when admin computes votes
        };

        // Check if user document exists
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
            // Check if already voted for this rink
            const votes = userDoc.data().votes || [];
            if (votes.some(v => v.rinkId === rinkId)) {
                throw new Error('Already voted for this rink');
            }

            // Add vote to array
            await updateDoc(userDocRef, {
                votes: arrayUnion(vote)
            });
        } else {
            // Create new user document
            await setDoc(userDocRef, {
                userId,
                votes: [vote],
                contributions: []
            });
        }

        return { success: true };
    } catch (error) {
        console.error('Error adding vote:', error);
        throw error;
    }
};

/**
 * Get vote count from rink data (stored in aggregatedrinks)
 * @param {string} rinkId - ID of the rink
 * @param {string} entityType - 'location' or 'trail'
 */
export const getVoteCount = async (rinkId, entityType = 'location') => {
    // Vote count is now stored directly in the rink data
    // This function is kept for compatibility but doesn't query Firestore
    // The vote count should be read from the rink object passed from App.js
    return 0; // Placeholder - will be replaced by rink.voteCount
};

/**
 * Check if user has voted (from localStorage)
 * @param {string} rinkId - ID of the rink  
 * @param {string} userId - User ID
 * @param {string} entityType - 'location' or 'trail'
 */
export const hasUserVoted = async (rinkId, userId, entityType = 'location') => {
    try {
        const userDocRef = doc(db, 'user_contributions', userId);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
            return false;
        }

        const votes = userDoc.data().votes || [];
        return votes.some(v => v.rinkId === rinkId);
    } catch (error) {
        console.error('Error checking vote status:', error);
        return false;
    }
};

/**
 * Admin function: Compute all uncounted votes and update aggregatedrinks
 */
export const computeVotes = async () => {
    // This will be implemented in admin dashboard
    // It reads all user_contributions, counts uncounted votes per rink,
    // updates aggregatedrinks with new counts, and marks votes as counted
    throw new Error('Use admin dashboard to compute votes');
};
